import { useEffect, useRef, useState } from "react";
import { LayoutGrid, Rows3 } from "lucide-react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

/** AlbaVox TrySoro embed ID from the Soro dashboard. */
export const TRYSORO_EMBED_ID = "3f2e7b9d-d4b7-4afd-9c70-44b187f2f741";

const SCRIPT_ATTR = "data-albavox-trysoro";
const VIEW_STORAGE_KEY = "albavox-blog-view";

export type BlogViewMode = "list" | "cards";

function readStoredView(): BlogViewMode {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "list" || stored === "cards") return stored;
  } catch {
    /* ignore */
  }
  return "list";
}

function readDocumentTheme(): "light" | "dark" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

function buildEmbedSrc(
  theme: "light" | "dark",
  postId: string | null,
): string {
  const url = new URL(
    `https://app.trysoro.com/api/embed/${TRYSORO_EMBED_ID}`,
  );
  // Always set theme explicitly (light and dark).
  url.searchParams.set("theme", theme);
  if (postId) url.searchParams.set("post", postId);
  // Bust CDN/browser cache — stale empty embeds caused the "60 minutes" message.
  url.searchParams.set("_", String(Date.now()));
  return url.toString();
}

/**
 * Soro injects separate style tags (`soro-blog-styles-light` / `-dark`) and
 * only inserts each once. If both remain in <head>, dark CSS wins after a
 * dark→light switch. Always strip them before reloading.
 */
function removeSoroArtifacts(): void {
  document
    .querySelectorAll(`script[${SCRIPT_ATTR}]`)
    .forEach((node) => node.remove());

  document
    .querySelectorAll('style[id^="soro-blog-styles"]')
    .forEach((node) => node.remove());

  document
    .querySelectorAll('link[id^="soro-blog-styles"]')
    .forEach((node) => node.remove());
}

/**
 * Loads the TrySoro blog embed into a stable mount node.
 * Rebuilds fully on light/dark changes so styles cannot stack.
 */
export function TrySoroBlog() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const postId = searchParams.get("post");
  const [theme, setTheme] = useState<"light" | "dark">(() =>
    typeof document === "undefined" ? "light" : readDocumentTheme(),
  );
  const [view, setView] = useState<BlogViewMode>("list");

  useEffect(() => {
    setView(readStoredView());
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setTheme(readDocumentTheme());
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const changeView = (next: BlogViewMode) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const showViewToggle = !postId;

  return (
    <div className="mt-10 sm:mt-12">
      {showViewToggle ? (
        <div className="mb-5 flex items-center justify-end">
          <div
            className="inline-flex rounded-lg border border-border-soft bg-bg-subtle p-1"
            role="group"
            aria-label={t("blog.view_label", { defaultValue: "View" })}
          >
            <button
              type="button"
              onClick={() => changeView("list")}
              aria-pressed={view === "list"}
              className={
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors " +
                (view === "list"
                  ? "bg-bg-base text-text-heading shadow-sm"
                  : "text-text-muted hover:text-text-heading")
              }
            >
              <Rows3 className="size-4" aria-hidden />
              {t("blog.view_list", { defaultValue: "List" })}
            </button>
            <button
              type="button"
              onClick={() => changeView("cards")}
              aria-pressed={view === "cards"}
              className={
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors " +
                (view === "cards"
                  ? "bg-bg-base text-text-heading shadow-sm"
                  : "text-text-muted hover:text-text-heading")
              }
            >
              <LayoutGrid className="size-4" aria-hidden />
              {t("blog.view_cards", { defaultValue: "Cards" })}
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="relative min-h-[16rem]"
        data-blog-view={showViewToggle ? view : "list"}
      >
        <TrySoroEmbedInstance
          key={`${theme}:${postId ?? ""}`}
          theme={theme}
          postId={postId}
          loadingLabel={t("blog.loading", { defaultValue: "Loading posts…" })}
          errorLabel={t("blog.load_error", {
            defaultValue:
              "We couldn’t load the blog right now. Please refresh and try again.",
          })}
        />
      </div>
    </div>
  );
}

type InstanceProps = {
  theme: "light" | "dark";
  postId: string | null;
  loadingLabel: string;
  errorLabel: string;
};

function TrySoroEmbedInstance({
  theme,
  postId,
  loadingLabel,
  errorLabel,
}: InstanceProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;
    let loadFallbackTimer = 0;
    let observer: MutationObserver | null = null;

    removeSoroArtifacts();
    mount.replaceChildren();
    mount.id = "soro-blog";
    setStatus("loading");

    const markReady = () => {
      if (cancelled) return;
      setStatus("ready");
      observer?.disconnect();
      observer = null;
    };

    // Prefer detecting real Soro content over a blind timeout.
    observer = new MutationObserver(() => {
      if (mount.querySelector(".soro-blog")) markReady();
    });
    observer.observe(mount, { childList: true, subtree: true });

    const script = document.createElement("script");
    script.src = buildEmbedSrc(theme, postId);
    script.async = true;
    script.defer = true;
    script.setAttribute(SCRIPT_ATTR, "true");

    script.onload = () => {
      if (cancelled) return;
      // Fallback if MutationObserver misses the paint.
      loadFallbackTimer = window.setTimeout(markReady, 800);
    };

    script.onerror = () => {
      if (cancelled) return;
      observer?.disconnect();
      setStatus("error");
    };

    mount.insertAdjacentElement("afterend", script);

    return () => {
      cancelled = true;
      window.clearTimeout(loadFallbackTimer);
      observer?.disconnect();
      removeSoroArtifacts();
      if (mount.isConnected) mount.replaceChildren();
    };
  }, [theme, postId]);

  return (
    <>
      {status === "loading" ? (
        <p
          className="absolute inset-x-0 top-8 z-10 text-center text-sm text-text-subtle"
          aria-live="polite"
        >
          {loadingLabel}
        </p>
      ) : null}

      {status === "error" ? (
        <p
          className="rounded-xl border border-border-soft bg-bg-subtle px-4 py-6 text-center text-sm text-text-muted dark:bg-bg-muted"
          role="alert"
        >
          {errorLabel}
        </p>
      ) : null}

      <div
        ref={mountRef}
        id="soro-blog"
        data-theme={theme}
        className={status === "error" ? "hidden" : undefined}
      />
    </>
  );
}
